import { toTitleCaseUi } from "@/shared/utils/titleCase";
import clsx from "clsx";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";

import type { BaseInputProps } from "@/shared/types";
import { getThemeClass, getThemeComponentGroup } from "../lib/themeComponentClasses";
import { WrapperInput } from "./WrapperInput";

type TextInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  formatValueOnBlur?: (value: string) => string;
  formatValueOnChange?: (value: string) => string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
};

export const inputClassName =
  "h-9 w-full rounded-xl border px-4 text-[13px] outline-none transition disabled:cursor-not-allowed disabled:opacity-60";

export function TextInput<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  formatValueOnBlur,
  formatValueOnChange,
  inputMode,
  maxLength,
  required,
  className,
  type = "text",
}: TextInputProps<TFieldValues, TName>) {
  const inputClasses = getThemeComponentGroup("input");
  const placeholderText = toTitleCaseUi(placeholder || "");

  const getDigitCountBeforeCursor = (value: string, position: number) =>
    value.slice(0, position).replace(/\D/g, "").length;

  const getFormattedCursorPosition = (
    formattedValue: string,
    digitCount: number,
    endsWithSeparator: boolean
  ) => {
    let position = 0;
    let count = 0;

    while (position < formattedValue.length && count < digitCount) {
      if (/\d/.test(formattedValue[position])) {
        count++;
      }
      position++;
    }

    // Preserve the cursor after a separator if it was there originally.
    if (endsWithSeparator) {
      while (position < formattedValue.length && !/\d/.test(formattedValue[position])) {
        position++;
      }
    }

    return position;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <WrapperInput
          label={label}
          error={fieldState.error?.message}
          description={description}
          required={required}
          className={className}
        >
          <input
            {...field}
            id={field.name}
            data-testid={field.name}
            value={field.value ?? ""}
            type={type}
            inputMode={inputMode}
            maxLength={maxLength}
            placeholder={placeholderText}
            disabled={disabled}
            // onChange={(event) => {
            //   if (!formatValueOnChange) {
            //     field.onChange(event);
            //     return;
            //   }

            //   field.onChange(formatValueOnChange(event.target.value));
            // }}
            onChange={(event) => {
              if (!formatValueOnChange) {
                field.onChange(event);
                return;
              }

              const input = event.currentTarget;
              const rawValue = input.value;

              const selectionStart = input.selectionStart ?? rawValue.length;
              const selectionEnd = input.selectionEnd ?? selectionStart;

              const startDigitCount = getDigitCountBeforeCursor(rawValue, selectionStart);

              const endDigitCount = getDigitCountBeforeCursor(rawValue, selectionEnd);

              const startEndsWithSeparator = /\D$/.test(rawValue.slice(0, selectionStart));

              const endEndsWithSeparator = /\D$/.test(rawValue.slice(0, selectionEnd));

              const formattedValue = formatValueOnChange(rawValue);

              field.onChange(formattedValue);

              if (type === "number") return;

              requestAnimationFrame(() => {
                const newStart = getFormattedCursorPosition(
                  formattedValue,
                  startDigitCount,
                  startEndsWithSeparator
                );

                const newEnd = getFormattedCursorPosition(
                  formattedValue,
                  endDigitCount,
                  endEndsWithSeparator
                );

                input.setSelectionRange(newStart, newEnd);
              });
            }}
            onBlur={(event) => {
              field.onBlur();

              if (!formatValueOnBlur) {
                return;
              }

              const formattedValue = formatValueOnBlur(event.target.value);

              if (formattedValue !== event.target.value) {
                field.onChange(formattedValue);
              }
            }}
            className={clsx(
              inputClassName,
              getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
              getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
              getThemeClass(inputClasses.textColor, "text-theme-text"),
              getThemeClass(
                inputClasses.placeholderColor,
                "placeholder:text-theme-text-placeholder"
              ),
              getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
              "focus:ring-2",
              getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
              inputClasses.base,
              fieldState.error &&
                clsx(
                  inputClasses.errorBorderColor || "border-theme-danger-bright",
                  "focus:ring-theme-danger-bright/10"
                )
            )}
          />
        </WrapperInput>
      )}
    />
  );
}
